import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import {
  MOBILE_PAGE_PAD_X,
  MOBILE_TOUCH_MIN_PX,
  MOBILE_TYPE,
} from "../../theme/mobileLayout";

/**
 * Generic mobile bottom action sheet (overflow menus, confirmations list).
 */
export default function MobileActionSheet({
  open,
  onClose,
  title,
  actions = [],
  cancelLabel = "Cancel",
}) {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        "data-testid": "mobile-action-sheet",
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom, 0px)",
          maxHeight: "85vh",
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: 999,
          bgcolor: "rgba(88, 89, 91, 0.28)",
          mx: "auto",
          mt: 1.25,
          mb: 1,
        }}
        aria-hidden
      />
      {title ? (
        <Typography
          sx={{
            ...MOBILE_TYPE.sectionTitle,
            px: MOBILE_PAGE_PAD_X,
            pb: 1,
            color: "text.secondary",
          }}
        >
          {title}
        </Typography>
      ) : null}
      <List disablePadding sx={{ px: 1 }}>
        {actions.map((action) => (
          <ListItemButton
            key={action.id || action.label}
            disabled={action.disabled}
            onClick={() => {
              action.onClick?.();
              if (action.closeOnClick !== false) onClose?.();
            }}
            sx={{
              minHeight: MOBILE_TOUCH_MIN_PX,
              borderRadius: 2,
              color: action.destructive ? "error.main" : "text.primary",
            }}
          >
            {action.icon ? (
              <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                {action.icon}
              </ListItemIcon>
            ) : null}
            <ListItemText
              primary={action.label}
              primaryTypographyProps={{
                sx: { ...MOBILE_TYPE.body, fontWeight: 600 },
              }}
              secondary={action.description}
              secondaryTypographyProps={{
                sx: { ...MOBILE_TYPE.helper },
              }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ px: MOBILE_PAGE_PAD_X, pb: 1.5 }}>
        <Button
          fullWidth
          onClick={onClose}
          sx={{
            minHeight: MOBILE_TOUCH_MIN_PX,
            ...MOBILE_TYPE.button,
            borderRadius: 2,
          }}
        >
          {cancelLabel}
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

MobileActionSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    destructive: PropTypes.bool,
    closeOnClick: PropTypes.bool,
  })),
  cancelLabel: PropTypes.string,
};
